import { useTranslation } from 'next-i18next'
import { useNextSanityImage } from 'next-sanity-image'
import Image from 'next/image'
import Link from 'next/link'
import React, { memo, useMemo } from 'react'
import { Text, View } from 'react-native'
import { tv } from 'tailwind-variants'
import { z } from 'zod'

import UiKit from '@nxt-insurance/ui-kit/index.native'

import { sanityClientLike } from '@/cms/client'
import { imageSchema } from '@/cms/image'
import { webUrlSchema } from '@/cms/webUrl'
import { useHyphenatedText } from '@/hooks/useHyphenatedText'
import { useUrlFromWebUrlReference } from '@/hooks/useUrlFromWebUrlReference'
import type { PortableTextTypeComponentProps } from '@portabletext/react'
import { body1, h3 } from '@ui-kit/core/typography'

const productCardStyles = tv({
  slots: {
    container: [
      'flex h-full w-[274px] flex-col rounded-lg border border-ui-grey-50',
      'bg-ui-grey-ultra-light p-6 transition-colors lg:w-[360px] lg:rounded-2xl lg:px-8 lg:py-10 lg:hover:bg-ui-light',
    ],
    image: 'mb-4 h-12 w-12 lg:h-20 lg:w-20',
    title: [h3({ type: 'secondary' })],
    description: body1({ color: 'uiDarkMedium', className: 'pb-8' }),
    actions: 'mt-auto flex flex-row gap-x-4 gap-y-2.5 max-lg:flex-col',
    action: 'max-sm:w-full',
  },
  variants: {
    hyphenatedTitle: {
      true: {
        title: 'hyphens-manual',
      },
      false: {
        title: 'hyphens-auto',
      },
    },
  },
})

const productCardSimpleSchema = z.object({
  _type: z.literal('productCardSimple'),
  title: z.string(),
  description: z.string(),
  image: imageSchema,
  primaryAction: webUrlSchema,
  secondaryAction: webUrlSchema,
})

export type ProductCardSimpleSchema = z.infer<typeof productCardSimpleSchema>

const ProductCardSimpleRenderer = (props: { node: PortableTextTypeComponentProps<unknown> }) => {
  const { t } = useTranslation()
  const value = useMemo(() => productCardSimpleSchema.parse(props.node.value), [props.node.value])

  const imageProps = useNextSanityImage(sanityClientLike, value.image, {
    imageBuilder: (imageBuilder) =>
      imageBuilder.withOptions({
        maxWidth: 80,
        maxHeight: 80,
        focalPoint: value.image?.hotspot
          ? { x: value.image?.hotspot?.x, y: value.image?.hotspot?.y }
          : undefined,
      }),
  })

  const primaryAction = useUrlFromWebUrlReference(value.primaryAction)
  const secondaryAction = useUrlFromWebUrlReference(value.secondaryAction)

  const title = useHyphenatedText(value.title)

  const styles = productCardStyles({
    hyphenatedTitle: title.hyphenated,
  })

  return (
    <View className={styles.container()}>
      <Image alt="product image" {...imageProps} className={styles.image()} unoptimized />
      <Text className={styles.title()}>{title?.text}</Text>
      <Text className={styles.description()}>{value.description}</Text>
      <View className={styles.actions()}>
        <Link href={primaryAction} className={styles.action()}>
          <UiKit.Button label={t('common:productCard.primaryButtonLabel')} />
        </Link>
        <Link href={secondaryAction} className={styles.action()}>
          <UiKit.Button label={t('common:productCard.secondaryButtonLabel')} type="text" />
        </Link>
      </View>
    </View>
  )
}

export const ProductCardSimple = memo(ProductCardSimpleRenderer)
